import { useLocation } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTelegramAuth } from "./useTelegramAuth";

export interface UseHeaderReturn {
  // Language navigation
  getLanguagePath: (lang: string) => string;
  getPagePath: (page: string) => string;
  handleLanguageClick: (lang: string) => void;

  // Telegram auth
  user: ReturnType<typeof useTelegramAuth>["user"];
  showLoginWidget: boolean;
  telegramWidgetRef: (node: HTMLDivElement | null) => void;
  handleLoginClick: (e: React.MouseEvent) => void;
  handleLogout: () => void;
  setShowLoginWidget: (show: boolean) => void;
}

export function useHeader(): UseHeaderReturn {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, logout: handleLogout } = useTelegramAuth();
  const [showLoginWidget, setShowLoginWidget] = useState(false);
  const botUsername =
    import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "lotinuz_bot";

  const getLanguagePath = useCallback(
    (lang: string) => {
      const currentPath = location.pathname;
      const pathSegments = currentPath.split("/").filter(Boolean);

      // Remove existing language prefix if any
      const allowedLangs = ["uz", "ru", "en"];
      if (pathSegments.length > 0 && allowedLangs.includes(pathSegments[0])) {
        pathSegments.shift();
      }

      // Build new path
      if (lang === "oz" || lang === "default") {
        return pathSegments.length > 0 ? `/${pathSegments.join("/")}` : "/";
      } else {
        return `/${lang}${pathSegments.length > 0 ? `/${pathSegments.join("/")}` : ""}`;
      }
    },
    [location.pathname]
  );

  const getPagePath = useCallback(
    (page: string) => {
      const currentPath = location.pathname;
      const pathSegments = currentPath.split("/").filter(Boolean);

      // Check if first segment is a language
      const allowedLangs = ["uz", "ru", "en"];
      if (pathSegments.length > 0 && allowedLangs.includes(pathSegments[0])) {
        // Has language prefix, preserve it
        return `/${pathSegments[0]}/${page}`;
      } else {
        // No language prefix (default language)
        return `/${page}`;
      }
    },
    [location.pathname]
  );

  const handleLanguageClick = useCallback(
    (lang: string) => {
      // Guard for SSR: only call changeLanguage if it exists
      if (i18n?.changeLanguage) {
        i18n.changeLanguage(lang === "oz" ? "default" : lang);
      }
    },
    [i18n]
  );

  // Callback ref: fires when the div mounts (after portal renders)
  const telegramWidgetRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && !user) {
        node.innerHTML = "";

        const script = document.createElement("script");
        script.src = "https://telegram.org/js/telegram-widget.js?22";
        script.setAttribute("data-telegram-login", botUsername);
        script.setAttribute("data-size", "medium");
        script.setAttribute("data-onauth", "onTelegramAuth(user)");
        script.setAttribute("data-request-access", "write");
        script.async = true;
        node.appendChild(script);
      }
    },
    [botUsername, user]
  );

  const handleLoginClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (user) {
      } else {
        setShowLoginWidget(!showLoginWidget);
      }
    },
    [user, showLoginWidget, handleLogout]
  );

  return {
    getLanguagePath,
    getPagePath,
    handleLanguageClick,
    user,
    showLoginWidget,
    telegramWidgetRef,
    handleLoginClick,
    handleLogout,
    setShowLoginWidget,
  };
}
